import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import {
  specificationLinesQueryOptions,
  type SpecLinesGroup,
  type SpecLinesLocation,
  type SpecLinesLine,
} from "@/hooks/specifications";
import { SOURCE_CACHE_LIFETIME } from "@/hooks/source-cache-lifetime";
import type { specLinesPagingOptions } from "@/hooks/spec-lines-paging";
import { specificationVersionRoomsQueryKey } from "@/hooks/spec-editor-cache";
import { groupDisplayCursor } from "@/lib/spec-lines-display";

// The spec lines body's hooks (#695): the expansion map and each group's own
// state, the lines loader, the deep link's search, the load-error snackbar and
// the scroll region the frame provides. The details page and the Editor (#548)
// both mount them.
//
// **Expansion.** Which groups of a spec version are expanded — the donor's
// `useExpandCollapseGroups` (`shared/components/ExpandCollapseGroups.tsx:40-125`)
// and the per-group half of it in `Group.tsx:27-53`.
//
// **Two layers, as the donor has them.** The body owns an *expansion map* —
// group id to expanded, a default for groups it has not seen, and two pulses
// that Collapse all and Expand all raise. Each group owns its *own* state,
// seeded from the map when it mounts, and writes it back to the map 300ms after
// every change (the collapse's own duration, so the write never lands
// mid-animation — the donor's reason for keeping the state local at all). The
// map is how a group's state survives a search, which unmounts every group and
// mounts them again; the pulses are how the two buttons reach groups already on
// screen.
//
// The consequence the donor lives with, carried (#695 enumeration C17): a
// write to the map alone — the deep link's expand — reaches only groups that
// mount after it. A group already on screen follows its own state.

export interface ExpansionState {
  /** Every group seen so far, by id. */
  map: Readonly<Record<number, boolean>>;
  /** What a group the map has not seen is: the donor's `defaultExpanded` ref. */
  defaultExpanded: boolean;
  /** Raised by Collapse all, cleared by any group opening. */
  allGroupsCollapsed: boolean;
  /** Raised by Expand all, cleared by any group closing. */
  allGroupsExpanded: boolean;
}

export type ExpansionAction =
  | { type: "seen"; groupIds: readonly number[] }
  | { type: "collapseAll" }
  | { type: "expandAll" }
  | { type: "set"; groupId: number; expanded: boolean };

export const INITIAL_EXPANSION: ExpansionState = {
  map: {},
  defaultExpanded: true,
  allGroupsCollapsed: false,
  allGroupsExpanded: false,
};

const everyKey = (map: Readonly<Record<number, boolean>>, expanded: boolean) =>
  Object.fromEntries(Object.keys(map).map((id) => [id, expanded]));

export function expansionReducer(state: ExpansionState, action: ExpansionAction): ExpansionState {
  switch (action.type) {
    case "seen": {
      // New groups at the default; what the map already holds wins.
      const unseen = action.groupIds.filter((id) => !(id in state.map));
      if (unseen.length === 0) return state;
      const added = Object.fromEntries(unseen.map((id) => [id, state.defaultExpanded]));
      return { ...state, map: { ...added, ...state.map } };
    }
    case "collapseAll":
      return {
        map: everyKey(state.map, false),
        defaultExpanded: false,
        allGroupsCollapsed: true,
        allGroupsExpanded: false,
      };
    case "expandAll":
      return {
        map: everyKey(state.map, true),
        defaultExpanded: true,
        allGroupsCollapsed: false,
        allGroupsExpanded: true,
      };
    case "set": {
      const map = { ...state.map, [action.groupId]: action.expanded };
      return action.expanded
        ? { ...state, map, allGroupsCollapsed: false, defaultExpanded: true }
        : { ...state, map, allGroupsExpanded: false };
    }
  }
}

export function isGroupExpanded(state: ExpansionState, groupId: number): boolean {
  return state.map[groupId] ?? state.defaultExpanded;
}

/**
 * The Expand/Collapse control's question — over **every group handed to it**,
 * the ones past the display cursor included (`ExpandCollapseGroups.tsx:109-115`).
 */
export function anyGroupExpanded(state: ExpansionState, groupIds: readonly number[]): boolean {
  return groupIds.some((id) => isGroupExpanded(state, id));
}

export interface SpecGroupExpansion {
  isGroupExpanded: (groupId: number) => boolean;
  /** Any group of the ordering expanded: the control's label and arrow. */
  hasExpandedGroups: boolean;
  onGroupExpandCollapse: (groupId: number, expanded: boolean) => void;
  onCollapseAllGroups: () => void;
  onExpandAllGroups: () => void;
  allGroupsCollapsed: boolean;
  allGroupsExpanded: boolean;
}

/**
 * The body's map, over the groups of the ordering on screen — `undefined`
 * while a search is in flight, which (as in the donor) seeds nothing and reads
 * as no group expanded.
 */
export function useSpecGroupExpansion(groupIds: readonly number[] | undefined): SpecGroupExpansion {
  const [state, dispatch] = useReducer(expansionReducer, INITIAL_EXPANSION);
  const key = groupIds?.join(",");

  useEffect(() => {
    if (groupIds) dispatch({ type: "seen", groupIds });
    // The ids' content is the dependency, not the array's identity: every page
    // hands over a new array of the same groups.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const onGroupExpandCollapse = useCallback(
    (groupId: number, expanded: boolean) => dispatch({ type: "set", groupId, expanded }),
    [],
  );
  const onCollapseAllGroups = useCallback(() => dispatch({ type: "collapseAll" }), []);
  const onExpandAllGroups = useCallback(() => dispatch({ type: "expandAll" }), []);
  const isExpanded = useCallback((groupId: number) => isGroupExpanded(state, groupId), [state]);

  return {
    isGroupExpanded: isExpanded,
    hasExpandedGroups: anyGroupExpanded(state, groupIds ?? []),
    onGroupExpandCollapse,
    onCollapseAllGroups,
    onExpandAllGroups,
    allGroupsCollapsed: state.allGroupsCollapsed,
    allGroupsExpanded: state.allGroupsExpanded,
  };
}

/** The group collapse's duration, and the write-back's delay (`constants.ts:1`). */
export const GROUP_EXPAND_COLLAPSE_MS = 300;

/**
 * One group's own state — `Group.tsx:27-53`: seeded from the map once, written
 * back 300ms after every change (the mount included, which is how a group the
 * map has not seen yet enters it), and forced by the two pulses.
 *
 * `seed`, when given, is the group's state as it stands right now somewhere
 * else — the Editor's card in its slot, for the copy of it drawn in the air,
 * which the map can trail by up to the 300ms write-back (#805).
 */
export function useGroupExpansion(groupId: number, expansion: SpecGroupExpansion, seed?: boolean) {
  const { isGroupExpanded, onGroupExpandCollapse, allGroupsCollapsed, allGroupsExpanded } =
    expansion;
  // The donor's two pulse effects run at mount too, so a pulse already raised
  // wins over the map's seed.
  const [expanded, setExpanded] = useState(() =>
    seed !== undefined
      ? seed
      : allGroupsCollapsed
        ? false
        : allGroupsExpanded
          ? true
          : isGroupExpanded(groupId),
  );

  // One pending write at a time: a quick double toggle writes only where it
  // settled, and a group that unmounts mid-animation — a search replacing the
  // groups — writes at once rather than into the next group's life. The map
  // ends where the donor's uncleared timers leave it, the fold of a group
  // searched away included.
  const pendingWrite = useRef<(() => void) | null>(null);
  useEffect(() => {
    const write = () => onGroupExpandCollapse(groupId, expanded);
    pendingWrite.current = write;
    const timer = window.setTimeout(() => {
      pendingWrite.current = null;
      write();
    }, GROUP_EXPAND_COLLAPSE_MS);
    return () => window.clearTimeout(timer);
  }, [groupId, expanded, onGroupExpandCollapse]);
  useEffect(() => () => pendingWrite.current?.(), []);

  // The pulses, as they rise: the donor's `useEffect`s on each, adjusted
  // during render rather than in an effect.
  const [pulses, setPulses] = useState({ allGroupsCollapsed, allGroupsExpanded });
  if (
    pulses.allGroupsCollapsed !== allGroupsCollapsed ||
    pulses.allGroupsExpanded !== allGroupsExpanded
  ) {
    setPulses({ allGroupsCollapsed, allGroupsExpanded });
    if (allGroupsCollapsed && !pulses.allGroupsCollapsed) setExpanded(false);
    if (allGroupsExpanded && !pulses.allGroupsExpanded) setExpanded(true);
  }

  const toggle = useCallback(() => setExpanded((current) => !current), []);
  return { expanded, toggle };
}

// ---------------------------------------------------------------------------
// The scroll region
// ---------------------------------------------------------------------------

/** Provided by the details frame around its scroll region. */
export const SpecScrollRegionContext = createContext<HTMLElement | null>(null);

/**
 * The details page's scroll region — the donor's `scrollRef` (`Layout.tsx`) —
 * for what the frame holds. `null` for the first render, before it has mounted.
 */
export function useSpecScrollRegion(): HTMLElement | null {
  return useContext(SpecScrollRegionContext);
}

// ---------------------------------------------------------------------------
// The lines loader, the deep link and the load error
// ---------------------------------------------------------------------------

/** The deep link's paging interval (`SpecDetailsIsland.tsx:128-137`). */
const DEEP_LINK_PAGING_MS = 500;

/** One group as the body draws it: the group and its loaded lines, in order. */
export interface SpecLinesOrderingEntry {
  group: SpecLinesGroup;
  lines: SpecLinesLine[];
}

export interface SpecLinesLoader {
  /** The first page of this page view is on its way: the island's skeleton. */
  isInitialLoading: boolean;
  /** A new term's first page is on its way. */
  isSearching: boolean;
  /** A lines read failed — with `tellsZeroState`, the base view's read too. */
  isError: boolean;
  /**
   * The view's failed read — under `tellsZeroState`, the base view's when
   * that is the one that failed — or `null`: what a source of its own (#1024) reads
   * to tell its denial, which leaves for a landing, from a failure, which
   * raises the snackbar. The signed-in pages read only `isError`.
   */
  error: Error | null;
  /**
   * No group is drawn, and none is on its way — the donor's `isNoResults`
   * (`shared/data/loader.ts:64`), which counts a failed read in. What hides
   * Expand/Collapse (`ExpandCollapseGroups.tsx:16,24`; #554 D4 (ii)).
   */
  isEmpty: boolean;
  /** `isEmpty` less a failed read: what says "No results" (#710 E2). */
  isNoResults: boolean;
  /**
   * The Editor's `Get Started`: `isNoResults` over a truly empty version, not
   * an empty match (`isZeroStateView`, D1b). The details page does not read it.
   */
  isZeroState: boolean;
  /**
   * The Editor's failed-read face's `Refresh Page`: the failed read again — the
   * base view's when that is what failed, the next page when that is, else the
   * view's pages — never a reload
   * (D1a / D14). The details page does not read it.
   */
  retry: () => void;
  /** `retry` is running: the face's button spins. */
  isRetrying: boolean;
  /**
   * The Editor's `No results`: `isNoResults` that is not the Zero State —
   * held while a search over an uncached base view waits for the base view
   * to tell the two apart.
   */
  isEmptyMatch: boolean;
  showPaginationSkeleton: boolean;
  /** The groups and their lines — `undefined` before the first page and while searching. */
  ordering: SpecLinesOrderingEntry[] | undefined;
  /** The last group index drawn. */
  displayCursor: number;
  /** Fetch the next page, unless one is in flight, there is none, or a fetch failed. */
  loadMore: () => void;
  /**
   * The version's rooms in the session's order, for the Editor's locations
   * select (#810): seeded from the first page, then changed only by the
   * location verbs' answers (`useVersionRooms`).
   */
  rooms: SpecLinesLocation[];
  /**
   * Every group of the version, in its order: the base view's, which a search
   * page does not carry whole, else the drawn ones until the base view is
   * cached — the Editor's Edit and Duplicate group lists (#810 Assumption 9,
   * `EditLineDetails.tsx:26-37`).
   */
  versionGroups: SpecLinesGroup[];
}

/** What tells an empty version from an empty match. */
export interface ZeroStateFacts {
  /** The loader's `isEmpty`: nothing drawn, nothing on its way. */
  isEmpty: boolean;
  isError: boolean;
  /** The view's term, `""` for the base view. */
  search: string;
  /** Whether the cached base view holds no group; `undefined` when not cached. */
  baseViewEmpty: boolean | undefined;
}

/**
 * The Editor's `Get Started` — the donor's `isNoResults && count === 0`
 * (`shared/data/loader.ts:61-65`), which reads the **searched** view's count,
 * so a no-match search and a failed read showed it too. Owner-ruled (D1b):
 * only a truly empty version is the Zero State — the base view empty, or a
 * search over a base view already known empty. Any other empty match is No
 * results, and a failed read is neither (D1a).
 */
export function isZeroStateView({ isEmpty, isError, search, baseViewEmpty }: ZeroStateFacts) {
  if (!isEmpty || isError) return false;
  return search === "" ? true : baseViewEmpty === true;
}

const NO_ROOMS: SpecLinesLocation[] = [];

/** Whether two room lists read the same — the same rooms, names and order. */
export function sameRooms(
  held: readonly SpecLinesLocation[],
  incoming: readonly SpecLinesLocation[],
): boolean {
  return (
    held.length === incoming.length &&
    held.every(
      (room, index) => room.id === incoming[index]!.id && room.name === incoming[index]!.name,
    )
  );
}

/**
 * The lines query a loader reads from: the signed-in lines read's options, or
 * a source's own that answer the same page shape.
 */
export type SpecLinesQueryOptions = ReturnType<typeof specLinesPagingOptions>;

/**
 * Where a loader's lines come from when they are not the signed-in lines read
 * — the public details page's token-scoped read (#1024), projected onto the
 * same page shape so the derivations below stay the one set. Its options for
 * a term (`""` the base view) carry their own key, so nothing it answers lands
 * in, or is read from, a signed-in entry.
 */
export interface SpecLinesSource {
  queryOptions: (search: string) => SpecLinesQueryOptions;
}

/**
 * The version's room list for the session (#965 N12): the source reads it
 * once, from load-metadata, and changes it only from the location verbs'
 * answers (`loaders.py:168-179`, `objectCache.ts:84-90`, `LineLocations.tsx:
 * 46-47`). Here it is held under its own key, seeded from the view's first
 * lines page (name order) and written after that only by `applyEditorResult`
 * — so a later page or a search, each carrying the list in name order, never
 * re-sorts it. The key keeps the source's 60 s lifetime once unobserved, so a
 * reload, or a later arrival after it lapses, shows name order again (#856
 * D1).
 */
function useVersionRooms(
  specificationId: number,
  versionId: number,
  seed: SpecLinesLocation[] | undefined,
  /**
   * Off for a lines source of its own (#1024): the rooms key is a signed-in
   * one, and the Editor its only reader, so the loader neither reads nor seeds
   * it — `useQueries` over no query leaves no entry in the cache at all.
   */
  holds: boolean,
): SpecLinesLocation[] | undefined {
  const queryClient = useQueryClient();
  const queryKey = specificationVersionRoomsQueryKey(specificationId, versionId);
  const roomsQuery = queryOptions({ queryKey, queryFn: skipToken, ...SOURCE_CACHE_LIFETIME });
  const [rooms] = useQueries({ queries: holds ? [roomsQuery] : ([] as (typeof roomsQuery)[]) });
  const held = rooms?.data as SpecLinesLocation[] | undefined;
  useEffect(() => {
    if (holds && held === undefined && seed) queryClient.setQueryData(queryKey, seed);
    // The key's content is the dependency, not the array's identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, specificationId, versionId, held, seed, holds]);
  return held ?? seed;
}

/** The rooms as given, but the previous array while they read the same. */
function useStableRooms(next: SpecLinesLocation[] | undefined): SpecLinesLocation[] {
  const incoming = next ?? NO_ROOMS;
  const [held, setHeld] = useState(incoming);
  const same = sameRooms(held, incoming);
  if (!same) setHeld(incoming);
  return same ? held : incoming;
}

/**
 * The donor's `useSpecificationLinesLoader` over the lines page's infinite
 * query: the loading states, the ordering, the display cursor and the
 * load-more callback, as `loader.ts` derives them.
 */
export function useSpecLinesLoader(
  specificationId: number,
  versionId: number,
  search: string,
  {
    tellsZeroState = false,
    source,
  }: {
    /**
     * The Editor's: tell an empty version from an empty match under a term by
     * the base view — the cached one, or its first page read for the purpose
     * when a searched view comes back empty and the base view is not cached.
     */
    tellsZeroState?: boolean;
    /** The lines from a source of its own; absent, the signed-in lines read. */
    source?: SpecLinesSource;
  } = {},
): SpecLinesLoader {
  const linesQueryOptions =
    source?.queryOptions ??
    ((term: string): SpecLinesQueryOptions =>
      specificationLinesQueryOptions(specificationId, versionId, term));
  const query = useInfiniteQuery(linesQueryOptions(search));

  // RTK Query's `isLoading` is the first load of the page view, which a later
  // term's load is not — that one is `isSearchingLines`. TanStack keys each
  // term separately, so the page view's first answer is remembered here.
  const [everLoaded, setEverLoaded] = useState(false);
  if (query.data !== undefined && !everLoaded) setEverLoaded(true);
  const isInitialLoading = !everLoaded && query.isPending && query.isFetching;

  // `isFetching && (!currentData || !versionOrdering)` (`loader.ts:51-52`).
  const isSearching = !isInitialLoading && query.isFetching && query.data === undefined;
  const pages = query.data?.pages ?? [];
  const last = pages.at(-1) ?? null;

  // The pages concatenated, as they came: no page repeats a line an earlier
  // one holds — every later page sends the held ids and the server leaves
  // them out (#910) — so nothing here filters. The group list is the last
  // page's: under a term, the search's snapshot.
  const ordering = useMemo(() => {
    if (!last) return undefined;
    const linesByGroup = new Map<number, SpecLinesLine[]>();
    for (const page of pages) {
      for (const line of page?.lines ?? []) {
        const lines = linesByGroup.get(line.groupId) ?? [];
        lines.push(line);
        linesByGroup.set(line.groupId, lines);
      }
    }
    return last.groups.map((group) => ({ group, lines: linesByGroup.get(group.id) ?? [] }));
    // `pages` is derived from `query.data`, which is the identity that changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  const cursor = last?.cursor ?? 0;
  const count = last?.total ?? 0;
  const hasMore = Boolean(last?.hasMore);
  const displayCursor = groupDisplayCursor(
    (ordering ?? []).map((entry) => ({
      lineCount: entry.group.lineCount,
      loadedLines: entry.lines.length,
    })),
    cursor,
    count,
  );

  const shownOrdering = isSearching ? undefined : ordering;
  const hasGroups = (shownOrdering?.length ?? 0) > 0;
  const isEmpty = !isInitialLoading && !isSearching && !hasGroups && !displayCursor;
  // The donor's rule, less a failed read (#710 E2, owner-ruled): its "No
  // results" under the load error reads as an empty spec.
  const isNoResults = isEmpty && !query.isError;
  // The base view under a term: the cached one, never refetched for this, or
  // — the donor's `count === 0` reading an empty version as one under any term
  // (`loader.ts:61-65`) — its first page, asked for only once the searched
  // view has come back empty.
  const baseQuery = useInfiniteQuery({
    ...linesQueryOptions(""),
    enabled: tellsZeroState && search !== "" && isNoResults,
    staleTime: Infinity,
  });
  const baseView = search === "" ? query.data : baseQuery.data;
  const baseLast = baseView?.pages.at(-1);
  // Every write lands a new list, so the rooms keep their identity until a
  // room itself changes — the line rows' slots close over them, and a new
  // array would re-render every memoized line on each write.
  const rooms = useStableRooms(
    useVersionRooms(specificationId, versionId, pages[0]?.locations, source === undefined),
  );
  const baseGroups = baseLast?.groups;
  const versionGroups = useMemo(() => baseGroups ?? last?.groups ?? [], [baseGroups, last]);
  const isZeroState = isZeroStateView({
    isEmpty,
    isError: query.isError,
    search,
    baseViewEmpty: baseLast === undefined ? undefined : (baseLast?.groups.length ?? 0) === 0,
  });
  const zeroStateKnown = !tellsZeroState || search === "" || baseLast !== undefined;
  const isEmptyMatch = isNoResults && !isZeroState && zeroStateKnown;
  // A failed base read cannot tell Get Started from No results: it is a failed
  // lines read like any other, and its face retries it. A query with no data
  // goes back to pending while it retries, so a failure already seen keeps
  // the face up, spinning, until the retry lands.
  const isBaseError =
    baseQuery.isEnabled &&
    baseLast === undefined &&
    (baseQuery.isError || (baseQuery.errorUpdateCount > 0 && baseQuery.isFetching));
  const showPaginationSkeleton = !isSearching && count > 0 && hasMore && !query.isError;

  // The live flags, for a callback a timer may run after they have moved on:
  // the observer's 300ms settle holds the callback it was built with.
  const live = useRef({ hasMore, isFetching: query.isFetching, isError: query.isError });
  useEffect(() => {
    live.current = { hasMore, isFetching: query.isFetching, isError: query.isError };
  });
  const { fetchNextPage } = query;
  const loadMore = useCallback(() => {
    const now = live.current;
    if (!now.hasMore || now.isFetching || now.isError) return;
    void fetchNextPage({ cancelRefetch: false });
    // Rebuilt whenever the flags move, as the donor's `useCallback` is — which
    // is what re-arms the observer after each page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNextPage, hasMore, query.isFetching, query.isError]);

  const { refetch, isFetchNextPageError } = query;
  const { refetch: refetchBase } = baseQuery;
  const retry = useCallback(() => {
    if (isBaseError) void refetchBase();
    else if (isFetchNextPageError) void fetchNextPage({ cancelRefetch: false });
    else void refetch();
  }, [fetchNextPage, refetch, refetchBase, isBaseError, isFetchNextPageError]);

  return {
    isInitialLoading,
    isSearching,
    isError: query.isError || isBaseError,
    error: query.error ?? (isBaseError ? baseQuery.error : null),
    isEmpty,
    isNoResults,
    isZeroState,
    retry,
    isRetrying: (query.isError && query.isFetching) || (isBaseError && baseQuery.isFetching),
    isEmptyMatch,
    showPaginationSkeleton,
    ordering: shownOrdering,
    displayCursor,
    loadMore,
    rooms,
    versionGroups,
  };
}

/**
 * The deep link's search for its line — the island's two effects
 * (`SpecDetailsIsland.tsx:96-139`). With a `lineId` and an ordering, it finds
 * the group holding the line and expands it through the map (reaching only a
 * group that mounts after, as the donor's does), or, not finding it, pages on:
 * at once, then every 500ms while it is still looking. With a `lineId` and no
 * ordering yet — a search in flight — it is looking. It stops looking only when
 * it finds the line; pages exhausted without it, the donor looks forever and
 * its overlay never lifts (#695 D1, built as the source until ruled).
 */
export function useDeepLinkSearch(
  lineId: number | undefined,
  loader: SpecLinesLoader,
  expansion: SpecGroupExpansion,
): boolean {
  const [looking, setLooking] = useState(false);
  const { ordering, showPaginationSkeleton, loadMore } = loader;
  const { isGroupExpanded, onGroupExpandCollapse } = expansion;

  const found = useMemo(
    () =>
      lineId !== undefined && ordering
        ? (ordering.find((entry) => entry.lines.some((line) => line.id === lineId)) ?? null)
        : null,
    [lineId, ordering],
  );

  // Effect A's `looking`, adjusted during render: found, it stops; not found
  // with a page left, it starts; with no ordering yet, it looks; with no
  // `lineId`, it does not. Not found with no page left — every page read, or
  // paging stopped by an error — it stops too: the donor's overlay never lifts
  // there, and the owner ruled that a parity break to take (#695 D1).
  let next = looking;
  if (lineId === undefined) next = false;
  else if (!ordering) next = true;
  else if (found) next = false;
  else if (!showPaginationSkeleton) next = false;
  else if (!looking) next = true;

  // Effect A asks for the next page in the same breath as it starts looking
  // over an ordering, not when it starts for want of one: a request, counted,
  // which the effect below answers once.
  const [pageRequests, setPageRequests] = useState(0);
  if (next !== looking) {
    setLooking(next);
    if (next && ordering) setPageRequests((count) => count + 1);
  }
  const answeredRequests = useRef(0);
  useEffect(() => {
    if (answeredRequests.current === pageRequests) return;
    answeredRequests.current = pageRequests;
    loadMore();
  }, [pageRequests, loadMore]);

  // Effect A's map write for the found line's group.
  useEffect(() => {
    if (found && !isGroupExpanded(found.group.id)) onGroupExpandCollapse(found.group.id, true);
  }, [found, isGroupExpanded, onGroupExpandCollapse]);

  // Effect B: every 500ms while it is still looking and a page is left.
  useEffect(() => {
    if (!looking || !showPaginationSkeleton) return;
    const timer = window.setTimeout(loadMore, DEEP_LINK_PAGING_MS);
    return () => window.clearTimeout(timer);
  }, [looking, showPaginationSkeleton, loadMore]);

  return looking;
}

/**
 * The specifications domain's error snackbar — the donor's `ErrorSnackbar`
 * (`src/domains/specifications/shared/components/ErrorSnackbar.tsx:8-15`), the
 * one the details page raises when its lines fail to load and the Editor
 * raises for its own failures (#695).
 *
 * **Not the catalog's `ItemsError`** (`@/hooks/items-error`), though both are a
 * modsy `Notification` at `severity='error'` anchored bottom-centre: that one
 * adds `sx={{ mb: 4, fontWeight: 'bold' }}` and this one adds nothing, so the
 * message sits at the standard Alert's weight on the snackbar's own inset.
 *
 * **Permanent.** It passes `autoHideDuration={10000}` and no `onClose`, and
 * MUI's snackbar implements auto-hide by calling `onClose` — so, as on the item
 * details page, the timer is inert and the snackbar stays for the life of the
 * condition; there is no X either (the Alert draws one only when `closable`).
 * Sonner as the outlet is the standing owner-ruled break.
 */
export function useSpecErrorSnackbar(active: boolean, message: string) {
  useEffect(() => {
    if (!active) return;
    const id = toast.error(message, {
      duration: Infinity,
      position: "bottom-center",
      dismissible: false,
    });
    return () => {
      toast.dismiss(id);
    };
  }, [active, message]);
}
